import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  Heading,
  Progress,
  Stat,
  Text,
  useTheme,
  useColorModeValue,
  Popover,
  PopoverTrigger,
  PopoverContent,
  useDisclosure,
  Badge,
  Tooltip,
  Tag,
  TagLabel,
  TagRightIcon,
} from "@chakra-ui/react";
import {
  CollecticonChevronDownSmall,
  CollecticonCircleInformation,
  CollecticonEye,
  CollecticonEyeDisabled,
  CollecticonMap,
} from "@devseed-ui/collecticons-chakra";
import { LngLatBoundsLike } from "react-map-gl";
import bbox from "@turf/bbox";

import {
  LoadingDetectionCard,
  DeleteSceneDialog,
  DetectionCard,
  Map,
  Error,
  Loading,
  MapScenePreview,
  MapSpeciesLegend,
  InferencesLayer,
  SceneStatusTag,
  DownloadDetections,
  ShareSceneDialog,
  StatsCard,
  StatsNumber,
  StatsLabel,
  Overline,
  LoadingStatsCard,
} from "../../components";
import MetaDataCard, {
  MetaData,
  MetaDataHeader,
} from "../../components/MetaDataCard";
import Panel, {
  PanelBody,
  PanelFooter,
  PanelHeader,
} from "../../components/Panel";
import { RunModel } from "./components";
import { ModelRun } from "../../types/scenes";
import {
  formatDate,
  getLikelySpecies,
  getSortedSpecies,
  getSpeciesLabel,
} from "../../utils";
import { useScene, useSceneInferences } from "../../hooks";
import { Inference } from "../../types/inferences";
import useInferenceSummary from "./hooks/useInferenceSummary";
import ImageControls from "../../components/ImageControls";
import { DEFAULT_BRIGHTNESS, DEFAULT_CONTRAST } from "../../settings";
import { ModelStatusTag } from "../../components/SceneStatusTag";
import InferenceFilters from "../../components/InferenceFilters";
import { PANEL_SIZE } from "../../theme";
import { useInferenceFiltersContext } from "../../context/InferenceFiltersContext";

function SceneDetail() {
  const { sceneId } = useParams();
  const theme = useTheme();
  const {
    scene,
    error: sceneError,
    isPending: sceneIsPending,
    refetch,
  } = useScene(sceneId);
  const {
    isWhale,
    isValid,
    inferencesValidated,
    minConfidence,
    minCoastlineDistance,
    isFlagged,
    activeSceneId,
    setActiveSceneId,
    resetFilters,
    compositeMode,
    setCompositeMode,
  } = useInferenceFiltersContext();
  const {
    inferences,
    error: inferencesError,
    isPending: inferencesIsPending,
    validate,
  } = useSceneInferences(sceneId, {
    is_whale: isWhale,
    min_coastline_distance: minCoastlineDistance,
    min_confidence: minConfidence,
    is_valid: isValid,
    validated: inferencesValidated,
    composited: compositeMode,
    flagged: isFlagged,
  });

  useEffect(() => {
    if (sceneId !== activeSceneId) {
      resetFilters();
      setActiveSceneId(sceneId || "");
    }
  }, [sceneId, activeSceneId, setActiveSceneId, resetFilters]);

  const [validatingIds, setValidatingIds] = useState<number[]>([]);
  const { count, validated, species, speciesValidated, modelRuns } =
    useInferenceSummary(inferences?.features);
  const inferencesSorted = useMemo(
    () =>
      inferences?.features.sort(
        (inferenceA: Inference, inferenceB: Inference) =>
          Number(getLikelySpecies(inferenceB).confidence) -
          Number(getLikelySpecies(inferenceA).confidence),
      ),
    [inferences],
  );
  const { isOpen, onToggle, onClose } = useDisclosure();
  const {
    isOpen: isFilterOpen,
    onToggle: onFiltersToggle,
    onClose: onFiltersClose,
  } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const {
    isOpen: isShareOpen,
    onOpen: onShareOpen,
    onClose: onShareClose,
  } = useDisclosure();

  const [brightness, setBrightness] = useState<number>(DEFAULT_BRIGHTNESS);
  const [contrast, setContrast] = useState<number>(DEFAULT_CONTRAST);
  const topSpecies = useMemo(() => {
    return getSortedSpecies(species);
  }, [species]);

  const detectionHeadingBg = useColorModeValue("#e7e8ea", "base.700");
  const mapLegendBg = useColorModeValue("base.50", "base.500");
  const filtersActive =
    isWhale !== undefined ||
    isValid !== undefined ||
    inferencesValidated !== undefined ||
    minConfidence > 0.1 ||
    minCoastlineDistance > 40 ||
    isFlagged !== undefined;
  const bounds: LngLatBoundsLike | undefined = useMemo(() => {
    if (!scene?.geometry) return;
    const [x1, y1, x2, y2] = bbox(scene);
    return [x1, y1, x2, y2];
  }, [scene]);

  if (sceneIsPending) {
    return <Loading mt="8" />;
  }

  if (!sceneId) {
    return <Error>Not found</Error>;
  }

  if (sceneError || inferencesError) {
    return <Error>{sceneError?.message || inferencesError?.message}</Error>;
  }
  const { is_valid, validation_error, is_shared: isShared, shared_with: collaborators, role, owner_email } = scene.properties;
  const hasSuccessModelRuns = scene.properties.model_runs.some(
    ({ status }: ModelRun) => status === "succeeded",
  );
  const validateInference = async (options: {
    validationId: number;
    isValid: boolean;
    compositeIds?: number[];
  }) => {
    const { validationId, isValid, compositeIds } = options;
    // compositeIds will be the array of database IDs if in composite mode, undefined otherwise
    const ids = compositeIds ?? [validationId];
    setValidatingIds((prev) => [...prev, ...ids]);
    await validate({ validationId, isValid, compositeIds });
    setValidatingIds((prev) => prev.filter((i) => !ids.includes(i)));
  };

  return (
    <Box
      display="grid"
      position="relative"
      gridTemplateColumns={`${PANEL_SIZE} 1fr`}
      overflow="hidden"
    >
      <Panel>
        <PanelHeader
          sceneDate={formatDate(new Date(Date.parse(scene.properties.date)))}
          closeLink="/scenes/"
          closeLabel="Return to the scenes dashboard"
          onDeleteScene={onDeleteOpen}
          onShareScene={onShareOpen}
          inferences={inferences}
          userRole={role}
          sceneId={sceneId}
          isSceneShared={isShared}
        >
          <SceneStatusTag scene={scene} />
        </PanelHeader>
        {role === "owner" && (
          <DeleteSceneDialog isOpen={isDeleteOpen} onClose={onDeleteClose} sceneId={sceneId} />
        )}
        <ShareSceneDialog
          isOpen={isShareOpen}
          onClose={onShareClose}
          sceneId={sceneId}
          collaborators={collaborators}
          refetchScene={refetch}
          role={role}
          ownerEmail={owner_email}
        />
        <PanelBody>
          {hasSuccessModelRuns && (
            <StatsCard>
              {inferencesIsPending ? (
                Array.from(Array(3)).map((i, ind) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <LoadingStatsCard key={ind} />
                ))
              ) : topSpecies.length > 0 ? (
                <>
                  <Box flexBasis="100%">
                    <Overline display="flex" alignItems="center" gap="1">
                      Validated /{" "}
                      <Text display="inline" opacity={0.5}>
                        Detected
                      </Text>
                    </Overline>
                  </Box>
                  {topSpecies.map(([label]) => {
                    const counts = speciesValidated[label] || {
                      validated: 0,
                      total: 0,
                    };
                    return (
                      <Stat key={label} textAlign="right" flex="initial">
                        <StatsNumber>
                          {counts.validated} /{" "}
                          <Box as="span" opacity="0.5">
                            {counts.total}
                          </Box>
                        </StatsNumber>
                        <StatsLabel>{getSpeciesLabel(label)}</StatsLabel>
                      </Stat>
                    );
                  })}
                  <Stat textAlign="right" flex="initial">
                    <StatsNumber>
                      {validated} /{" "}
                      <Box as="span" opacity="0.5">
                        {count}
                      </Box>
                    </StatsNumber>
                    <StatsLabel>Total</StatsLabel>
                  </Stat>
                </>
              ) : (
                <Overline>0 species detected</Overline>
              )}
              {(compositeMode || filtersActive) &&              
                <Box flexBasis="100%" display="flex" gap="2" alignItems="center" justifyContent="flex-end" mt="1">
                  {compositeMode && (
                    <Tooltip
                      label="Counts show grouped inferences (multiple detections of the same individual combined)"
                      fontSize="xs"
                    >
                      <Tag
                        rounded="sm"
                        size="sm"
                        fontSize="10px"
                        colorScheme="purple"
                      >
                        <TagLabel textTransform="uppercase" mt="0.75px">
                          Composite
                        </TagLabel>
                        <TagRightIcon
                          as={CollecticonCircleInformation}
                          boxSize="12px"
                        />
                      </Tag>
                    </Tooltip>
                  )}
                  {filtersActive && (
                    <Tooltip
                      label="Counts affected by currently applied filters"
                      fontSize="xs"
                    >
                      <Tag
                        rounded="sm"
                        size="sm"
                        fontSize="10px"
                        colorScheme="primary"
                      >
                        <TagLabel textTransform="uppercase" mt="0.75px">
                          Filtered
                        </TagLabel>
                        <TagRightIcon
                          as={CollecticonCircleInformation}
                          boxSize="12px"
                        />
                      </Tag>
                    </Tooltip>
                  )}
                </Box>}
            </StatsCard>
          )}

          <MetaDataCard>
            <MetaDataHeader as="h3">Scene Metadata</MetaDataHeader>
            <MetaData
              label="Scene Id"
              value={`${scene.properties.s3_prefix.substring(0, 20)}...`}
            />
            <MetaData
              label="Aquired"
              value={formatDate(new Date(Date.parse(scene.properties.date)))}
            />
            <MetaData
              label="Uploaded"
              value={formatDate(new Date(Date.parse(scene.properties.registered))).slice(0, 20)}
            />
            <MetaData
              label="Model Runs"
              value={scene.properties.model_runs.length}
            />
            <Box display="flex" fontSize="sm" gap="4" alignItems="center">
              <Box
                as="span"
                opacity="0.875"
                flex="1"
                display="flex"
                alignItems="center"
                gap="1"
              >
                Detections
                {compositeMode && (
                  <Tooltip
                    label="Counts show grouped inferences (multiple detections of the same individual combined)"
                    fontSize="xs"
                  >
                    <Tag
                      rounded="sm"
                      size="sm"
                      fontSize="10px"
                      colorScheme="purple"
                    >
                      <TagLabel textTransform="uppercase" mt="0.75px">
                        Composite
                      </TagLabel>
                      <TagRightIcon
                        as={CollecticonCircleInformation}
                        boxSize="12px"
                      />
                    </Tag>
                  </Tooltip>
                )}
                {filtersActive && (
                  <Tooltip
                    label="Counts affected by currently applied filters"
                    fontSize="xs"
                  >
                    <Tag
                      rounded="sm"
                      size="sm"
                      fontSize="10px"
                      colorScheme="primary"
                    >
                      <TagLabel textTransform="uppercase" mt="0.75px">
                        Filtered
                      </TagLabel>
                      <TagRightIcon
                        as={CollecticonCircleInformation}
                        boxSize="12px"
                      />
                    </Tag>
                  </Tooltip>
                )}
              </Box>
              <Box as="span">{count || 0}</Box>
            </Box>
            {count > 0 && (
              <>
                <Text mt={2} fontSize="sm" opacity="0.875">
                  Validated Detections ({validated} of {count})
                </Text>
                <Progress
                  value={(validated / count) * 100}
                  colorScheme="primary"
                  size="sm"
                  borderRadius="4"
                />
              </>
            )}
          </MetaDataCard>

          <RunModel sceneId={sceneId} onSubmit={refetch} />

          {scene.properties.model_runs.map((run: ModelRun) => {
            return (
              <MetaDataCard key={run.id}>
                <MetaDataHeader as="h3">Model Results</MetaDataHeader>
                <MetaData
                  label="Model"
                  value={`${run.model.name} (${run.model.version})`}
                />
                <MetaData
                  label="Last Run"
                  value={formatDate(new Date(Date.parse(run.date))).slice(0, 20)}
                />
                <Box display="flex" fontSize="sm" gap="4" alignItems="cen†er">
                  <Box as="span" opacity="0.875" flex="1">
                    Status
                  </Box>
                  <ModelStatusTag run={run} />
                </Box>
                <Box display="flex" fontSize="sm" gap="4" alignItems="center">
                  <Box
                    as="span"
                    opacity="0.875"
                    flex="1"
                    display="flex"
                    alignItems="center"
                    gap="1"
                  >
                    Detections
                    {compositeMode && (
                      <Tooltip
                        label="Counts show grouped inferences (multiple detections of the same individual combined)"
                        fontSize="xs"
                      >
                        <Tag
                          rounded="sm"
                          size="sm"
                          fontSize="10px"
                          colorScheme="purple"
                        >
                          <TagLabel textTransform="uppercase" mt="0.75px">
                            Composite
                          </TagLabel>
                          <TagRightIcon
                            as={CollecticonCircleInformation}
                            boxSize="12px"
                          />
                        </Tag>
                      </Tooltip>
                    )}
                    {filtersActive && (
                      <Tooltip
                        label="Counts affected by currently applied filters"
                        fontSize="xs"
                      >
                        <Tag
                          rounded="sm"
                          size="sm"
                          fontSize="10px"
                          colorScheme="primary"
                        >
                          <TagLabel textTransform="uppercase" mt="0.75px">
                            Filtered
                          </TagLabel>
                          <TagRightIcon
                            as={CollecticonCircleInformation}
                            boxSize="12px"
                          />
                        </Tag>
                      </Tooltip>
                    )}
                  </Box>
                  <Box as="span">{modelRuns[run.id]?.count || 0}</Box>
                </Box>
              </MetaDataCard>
            );
          })}
        </PanelBody>
        <PanelFooter>
          <Button
            variant="base-soft-outline"
            rightIcon={<CollecticonMap />}
            as={Link}
            to={`/scenes/${sceneId}/validate/`}
            isDisabled={!inferences?.features.length}
          >
            Validate in Map
          </Button>
          <DownloadDetections
            inferences={inferences}
            fileName={`scene-${
              scene.properties.date.split("Z")[0]
            }-inferences.csv`}
            sceneId={sceneId || ""}
            isDisabled={!inferences?.features.length || !sceneId}
          >
            Download Scene Detections
          </DownloadDetections>
        </PanelFooter>
      </Panel>
      <Box display="grid" gridTemplateRows="1fr 1fr" overflow="auto">
        <Map bounds={bounds} basemapSwitcher>
          <Card
            position="absolute"
            bottom={10}
            w="fit-content"
            minW="12rem"
            maxHeight="18rem"
            overflow="auto"
            left={2.5}
            p={3}
            fontFamily={theme.fonts.body}
            zIndex="1020"
            background={mapLegendBg}
            border="1px solid var(--chakra-colors-chakra-border-color)"
          >
            <MapSpeciesLegend />
          </Card>
          {!validation_error && is_valid && (
            <MapScenePreview
              id={sceneId}
              brightness={brightness}
              contrast={contrast}
            />
          )}
          {inferencesIsPending && (
            <Loading
              fontSize="sm"
              position="absolute"
              fontFamily={theme.fonts.body}
              top={6}
              right={6}
              display="flex"
              alignItems="center"
              gap={4}
              fontWeight="semibold"
            >
              Loading detections
            </Loading>
          )}
          {inferences && <InferencesLayer id="inferences" data={inferences} />}
        </Map>
        <Box
          p="4"
          borderTop="1px solid"
          borderColor="base.200a"
          overflow="auto"
          bg="base.100a"
        >
          <Box
            position="sticky"
            top="-1rem"
            zIndex="1005"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            background={detectionHeadingBg}
            margin="-1rem -1rem 1rem"
            padding="1rem 1rem"
            borderBottom="1px solid"
            borderColor={mapLegendBg}
            gap={4}
          >
            <Heading as="h3" fontSize="lg" mr="auto">
              Detections
            </Heading>
            <Button
              onClick={() => setCompositeMode(!compositeMode)}
              colorScheme="purple"
              variant={compositeMode ? "solid" : "base-soft-outline"}
              size="sm"
              leftIcon={
                compositeMode ? <CollecticonEye /> : <CollecticonEyeDisabled />
              }
              title={
                compositeMode
                  ? "Composite mode: Detection counts show grouped inferences"
                  : "Composite mode off"
              }
            >
              Composite Mode
            </Button>
            <Popover
              isOpen={isFilterOpen}
              onClose={onFiltersClose}
              placement="auto"
            >
              <PopoverTrigger>
                <Button
                  rightIcon={<CollecticonChevronDownSmall />}
                  onClick={onFiltersToggle}
                  variant="primary-soft-outline"
                  size="sm"
                >
                  Filters
                  {filtersActive && (
                    <Badge
                      size="sm"
                      fontSize="10px"
                      variant="solid"
                      colorScheme="primary"
                      ml="1"
                    >
                      On
                    </Badge>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent minW="fit-content" p={4}>
                <InferenceFilters />
              </PopoverContent>
            </Popover>
            <Popover isOpen={isOpen} onClose={onClose} placement="bottom-end">
              <PopoverTrigger>
                <Button
                  rightIcon={<CollecticonChevronDownSmall />}
                  onClick={onToggle}
                  variant="primary-soft-outline"
                  size="sm"
                >
                  Image Controls
                </Button>
              </PopoverTrigger>
              <PopoverContent w="16rem" p={4} pb={6}>
                <ImageControls
                  brightness={brightness}
                  setBrightness={setBrightness}
                  contrast={contrast}
                  setContrast={setContrast}
                />
              </PopoverContent>
            </Popover>
          </Box>
          <Box
            display="grid"
            gridTemplateColumns="repeat(auto-fill, minmax(12rem,1fr))"
            gap="4"
          >
            {inferencesSorted?.length ? (
              inferencesSorted.map((inference: Inference) => (
                <Card
                  key={inference.properties.id}
                  fontSize="sm"
                  position="relative"
                  display="flex"
                  flexDirection="column"
                  overflow="hidden"
                >
                  <DetectionCard
                    inference={inference}
                    validate={validateInference}
                    isValidating={
                      // Check if any of the IDs in this inference are being validated
                      inference.properties.ids
                        ? inference.properties.ids.some((id) =>
                          validatingIds.includes(id),
                        )
                        : inference.properties.id
                          ? validatingIds.includes(inference.properties.id)
                          : false
                    }
                    brightness={brightness}
                    contrast={contrast}
                    includeThumbnail
                    linkToValidate
                    compositeMode={compositeMode}
                  />
                </Card>
              ))
            ) : (
              <>
                {inferencesIsPending ? (
                  <Loading opacity="0.75" fontSize="sm" gridColumn="1 / -1">
                    Fetching detections
                  </Loading>
                ) : (
                  <Heading as="h4" opacity="0.75" size="sm" gridColumn="1 / -1">
                    {hasSuccessModelRuns
                      ? "No species detected"
                      : "No successful model runs"}
                  </Heading>
                )}
                {Array.from(Array(10)).map((a, ind) => (
                  <Card
                    // eslint-disable-next-line react/no-array-index-key
                    key={ind}
                    opacity="0.5"
                  >
                    <LoadingDetectionCard noData={!inferencesIsPending} />
                  </Card>
                ))}
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default SceneDetail;
